import React, {
  Fragment,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  encodeId,
  generateCalanderMonthViewEvent,
  handleError,
  getWeekDay,
  titleWordLimit,
} from "utils";
import moment from "moment";
import constants, {
  getSchedulerStatusById,
} from "../../../../../../../constants";
import styles from "./../../../../Scheduler.module.scss";
import Text from "components/Text";
import { withTranslation } from "react-i18next";
import {
  useGetOfficeEventMonthly,
  useGetNotesEventMonthly,
  duplicateSchedulerEvent,
  deleteSchedularNote,
  getAssignedEmployess,
  useCreateUpdateSchedulerEventMutation,
  getEventListDetails,
} from "repositories/scheduler-repository";
import { buildDuplicateSchedulerEventPayload } from "./duplicateSchedulerEvent.utils";
import { useMultipleDuplicatesFlyout } from "./useMultipleDuplicatesFlyout";
import { useAssignUsersFromCard } from "./useAssignUsersFromCard";
import AddNotesModal from "../Modals/AddNotesModal";
import EditNotesModal from "../Modals/EditNotesModal";
import DeleteNotes from "../Modals/DeleteNotes";
import Loader from "components/Loader";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import EmptySchedulerCellAdd from "accountOwner/pages/Scheduler/components/EmptySchedulerCellAdd";
import uniq from "lodash/uniq";
import AlertInfo from "../../../../../../../assets/images/alert_info.svg";
import { Link } from "react-router-dom";
import { ThreeDots } from "react-bootstrap-icons";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Tooltip,
  UncontrolledTooltip,
} from "reactstrap";
import {
  renderOfficeNameOrEnterprise,
  setterCustomSchduler,
  setterPublishOffices,
  matchedBothPatternValues,
  matchedBothDatesValues,
} from "accountOwner/pages/AddEvent/addEvents.functions";
import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import EditOptionsModal, {
  editTypesVariables,
} from "accountOwner/pages/EditEvent/EditOptionsModal";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { showTime } from "..";
import classNames from "classnames";
import HolidayImage from "assets/images/early_on.svg";
import AddedNote from "assets/images/note-added.svg";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import { canShowEventCardMenu } from "accountOwner/pages/Scheduler/scheduler.function";
import {
  sortSchedulerEventsByStartTime,
  isUnassignedSchedulerEvent,
} from "./unassignedEvents.utils";
import { collectDraftPublishItemsFromWeekData } from "./eventsWorkspacePublish.utils";
import { useSimpleEventCardDelete } from "./useSimpleEventCardDelete";
import {
  getEventShiftSelectionKey,
  isEventShiftSelected,
} from "./eventWeeklyLayout.constants";
const convertTags = (tags) => {
  return tags?.length ? tags.split(",") : [];
};
/** Minimum number of event cards a monthly date cell always keeps visible. */
const MIN_VISIBLE_MONTHLY_EVENTS = 1;
/** Sub-pixel slack so rounding never hides a card that actually fits. */
const MONTHLY_FIT_TOLERANCE = 1;
/**
 * Floor for the height of a date cell, roughly the room needed by the date
 * number plus one unfiltered event card. Used when the viewport is too short to
 * give every week of the month a fair share.
 */
const MONTHLY_MIN_CELL_HEIGHT = 120;
/** Height the calendar container aims for when nothing else constrains it. */
const MONTHLY_MIN_TABLE_HEIGHT = 400;
/**
 * Room inside the wrapper below which the measurement is treated as meaningless
 * - the sidebar is hidden or floating rather than sizing the wrapper - and the
 * calendar sizes itself from the viewport instead.
 */
const MONTHLY_MIN_ROOM = 200;
/** Gap kept between the bottom of the calendar and the bottom of the card. */
const MONTHLY_BOTTOM_GAP = 20;
/**
 * Date cells per row on a full-width grid. Only a fallback for when the grid
 * has not been laid out yet - the responsive styles drop the grid to 3 and then
 * 2 columns on narrower screens.
 */
const DAYS_PER_WEEK = 7;

/**
 * Counts the date cells the month grid currently puts on one row.
 *
 * Read from the rendered widths rather than from media queries so it stays
 * correct at every breakpoint without duplicating them here.
 *
 * @param {HTMLElement|null} rowElement flex container holding all date cells
 * @returns {number} cells per row, `DAYS_PER_WEEK` when it cannot be measured
 */
const getColumnsPerRow = (rowElement) => {
  const firstCell = rowElement?.firstElementChild;
  if (!firstCell) return DAYS_PER_WEEK;
  const rowWidth = rowElement.clientWidth;
  const cellWidth = firstCell.getBoundingClientRect().width;
  if (!rowWidth || !cellWidth) return DAYS_PER_WEEK;
  return Math.max(1, Math.round(rowWidth / cellWidth));
};

/**
 * Measures the room the calendar may occupy inside `.scheduler-tabs` without
 * making that card any taller.
 *
 * The card holds a flex row of the filter sidebar and the column the calendar
 * lives in, so it is as tall as whichever of the two is taller (or its own
 * `min-height`). Reading its height while the calendar is laid out would
 * therefore report the space the calendar is already taking up and let it grow
 * again on the next pass, so the calendar is collapsed for the measurement.
 * What is left is the height the card has on its own.
 *
 * @param {HTMLElement} container the calendar scroll container
 * @returns {number} room in pixels below the top of the calendar, `0` when the
 *   card cannot be found. Independent of the scroll position, since both edges
 *   are read from the same layout.
 */
const measureRoomInsideCard = (container) => {
  const wrapper =
    container.closest(".scheduler-tabs") ||
    container.closest(".scheduler-tabs-main-wrapper");
  if (!wrapper) return 0;

  const { scrollX, scrollY } = window;
  const previous = {
    maxHeight: container.style.maxHeight,
    height: container.style.height,
    overflowY: container.style.overflowY,
  };
  container.style.maxHeight = "0px";
  container.style.height = "0px";
  container.style.overflowY = "hidden";

  // Reading the rects here forces a synchronous layout, so the card is
  // measured while collapsed even though the styles are restored right after.
  const room =
    wrapper.getBoundingClientRect().bottom -
    container.getBoundingClientRect().top -
    MONTHLY_BOTTOM_GAP;

  container.style.maxHeight = previous.maxHeight;
  container.style.height = previous.height;
  container.style.overflowY = previous.overflowY;
  // Collapsing shortens the document, which can clamp the scroll position.
  if (window.scrollY !== scrollY) window.scrollTo(scrollX, scrollY);

  return Math.max(0, room);
};

/**
 * Measures the vertical room left for event cards inside a monthly date cell.
 *
 * The value is derived from the cell box and from where the cards container
 * starts (cell padding, date number, holiday banner, drag preview), so it stays
 * the same no matter how many cards are currently rendered. That is what makes
 * the fit calculation converge instead of chasing its own growth.
 *
 * @param {HTMLElement} cellElement date cell element
 * @param {HTMLElement} cardsElement in-flow container holding the event cards
 * @returns {number} available height in pixels for event cards
 */
const getAvailableCardsHeight = (cellElement, cardsElement) => {
  const cellRect = cellElement.getBoundingClientRect();
  const cardsRect = cardsElement.getBoundingClientRect();
  const cellStyle = window.getComputedStyle(cellElement);
  const bottomInset =
    (parseFloat(cellStyle.paddingBottom) || 0) +
    (parseFloat(cellStyle.borderBottomWidth) || 0);
  return Math.max(0, cellRect.bottom - bottomInset - cardsRect.top);
};

/**
 * "See more" affordance of a monthly date cell.
 *
 * Rendered only when the day has matching events that did not fit in the cell,
 * so display filters that shrink the cards remove the link as soon as every
 * matching event is visible.
 */
const SeeMoreButton = ({
  events,
  hiddenEventsCount,
  index,
  date,
  handleEventList,
  t,
}) => {
  if (!hiddenEventsCount) {
    return null;
  }
  return (
    <span
      className={"link-btn"}
      onClick={() => handleEventList(events, index, date)}
    >
      {" "}
      {t("SeeMore")}
    </span>
  );
};

const EventEmployee = ({ t, setAssignEmpModel, assignEmpModel }) => {
  return (
    <div
      className={`${styles["scheduler-popup-box"]} ${styles["employee-popup-box"]} }`}
    >
      <span
        className={styles["close-icon"]}
        onClick={() => setAssignEmpModel({})}
      >
        &times;
      </span>
      <div className={styles["emp-data-list"]}>
        {assignEmpModel?.list?.map((e, _index) => (
          <div className={styles["emp-data"]}>
            <div
              key={e?.user?.firstName + _index}
              className={styles["row-box"]}
            >
              <div className={styles.clinic_name_status}>
                <Text size="14px" color="#587E85" weight="600">
                  {e?.user?.firstName} {e?.user?.lastName}{" "}
                </Text>
                <span className={styles.bullet_text}>
                  <p className={styles.status_text}>
                    {t(getSchedulerStatusById(e?.status))}
                  </p>
                </span>
              </div>
              <div className={styles.clinic_role_status_wrapper}>
                <Text size="12px" color="#2F3245" weight="500">
                  {e?.office?.name}
                </Text>
                <span className={styles.bullet_text}>
                  <Text size="12px" color="#2F3245" weight="500">
                    {e?.user?.role?.systemRole === 2
                      ? e?.user?.role?.name
                      : e?.user?.designation?.name}
                  </Text>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
const EventCard = ({
  parentIndex,
  list,
  assignEmpModel,
  eventCardListing,
  setEventCardListing,
  setAssignEmpModel,
  t,
  holidayElements,
  moveToDetailPage,
  viewSettingFilter,
  assignedEmployeesToggle,
  maxVisibleEvents,
  classFOrHoliday,
  history,
  canDragEvent,
  handleDragStart,
  handleDragEnd,
  isDragging,
  draggedEvent,
  onDuplicateEvent,
  onAssignUsers,
  onDeleteEvent,
  bulkSelectMode,
  selectedEvents,
  handleEventSelectionToggle,
}) => {
  const listing = list;

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const shouldExpand =
      assignEmpModel?.type === constants.PopUp.innerPopUp &&
      assignEmpModel?.date === eventCardListing?.date;
    setIsExpanded(shouldExpand);
  }, [assignEmpModel, eventCardListing]);

  // The popup only lists what the cell could not fit, so close it when a filter
  // change made every event of the day visible in the cell itself.
  useEffect(() => {
    if ((listing?.length ?? 0) <= maxVisibleEvents) {
      setEventCardListing({});
    }
  }, [listing?.length, maxVisibleEvents, setEventCardListing]);

  return (
    <div className={styles["monthly-event-list"] + " " + classFOrHoliday}>
      <span
        className={styles["close-icon-list"]}
        onClick={() => {
          setEventCardListing({});
        }}
      >
        <img
          src={require("assets/images/popup-cross-icon.svg").default}
          alt="icon"
        />
      </span>

      <div className={styles["day-date-box"]}>
        <div className={styles["day-name"]}>
          {getWeekDay(eventCardListing?.date)}
        </div>
        <div className={styles["date-name"]}>
          {moment(eventCardListing?.date).format("DD")}
        </div>
      </div>

      <div
        className={classNames(styles["event-content-container"], {
          [styles.expanded]: isExpanded,
        })}
      >
        {/* Left: Event Boxes */}
        <div
          className={
            styles["event-box-wrapper"] + " w-100 " + styles.min_width_168
          }
        >
          {listing?.map((item, cardIndex) => {
            if (maxVisibleEvents > cardIndex) return null;
            return (
              <EventBox
                key={eventCardListing?.date + "box" + cardIndex}
                item={item}
                index={cardIndex}
                date={eventCardListing?.date}
                parentIndex={parentIndex}
                type={constants.PopUp.innerPopUp}
                scrollClass={item?.activeClass}
                t={t}
                holidayElements={holidayElements}
                moveToDetailPage={moveToDetailPage}
                viewSettingFilter={viewSettingFilter}
                assignEmpModel={assignEmpModel}
                assignedEmployeesToggle={assignedEmployeesToggle}
                onDuplicateEvent={onDuplicateEvent}
                onAssignUsers={onAssignUsers}
                onDeleteEvent={onDeleteEvent}
                history={history}
                canDragEvent={canDragEvent}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                isDragging={isDragging}
                draggedEvent={draggedEvent}
                bulkSelectMode={bulkSelectMode}
                selectedEvents={selectedEvents}
                handleEventSelectionToggle={handleEventSelectionToggle}
              />
            );
          })}
        </div>

        {/* Right: Employee Panel (always rendered) */}
        <div
          className={styles["employee-data-wrapper"]}
          style={{
            opacity: isExpanded ? 1 : 0,
            pointerEvents: isExpanded ? "auto" : "none",
            width: isExpanded ? "100%" : "0",
            padding: isExpanded ? "8px" : "0",
          }}
        >
          <span
            className={styles["close-icon"]}
            onClick={() => setAssignEmpModel({})}
          >
            <img
              src={require("assets/images/close-icon.svg").default}
              alt="icon"
            />
          </span>
          <div className={styles["emp-data-list"]}>
            {assignEmpModel?.list?.map((e, _index) => (
              <div className={styles["emp-data"]} key={_index}>
                <div className={styles["row-box"]}>
                  <div className={styles.name_status_cont}>
                    <Text size="14px" color="#587E85" weight="600">
                      {e?.user?.firstName} {e?.user?.lastName}
                    </Text>
                    <span className={styles.bullet_text}>
                      <p className={styles.status_text}>
                        {t(getSchedulerStatusById(e?.status))}
                      </p>
                    </span>
                  </div>
                  <div className={styles.clinic_role_status_wrapper}>
                    <Text size="12px" color="#2F3245" weight="500">
                      {e?.office?.name}
                    </Text>
                    {(e?.user?.role?.systemRole === 2 && e?.user?.role?.name) ||
                    e?.user?.designation?.name ? (
                      <span className={styles.bullet_text}>
                        <Text size="12px" color="#2F3245" weight="500">
                          {e?.user?.role?.systemRole === 2
                            ? e?.user?.role?.name
                            : e?.user?.designation?.name}
                        </Text>
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const EventBox = ({
  item,
  index,
  date,
  type,
  moveToDetailPage,
  scrollClass,
  viewSettingFilter,
  assignEmpModel,
  t,
  assignedEmployeesToggle,
  history,
  canDragEvent,
  handleDragStart,
  handleDragEnd,
  isDragging,
  draggedEvent,
  onDuplicateEvent,
  onAssignUsers,
  onDeleteEvent,
  bulkSelectMode = false,
  selectedEvents = {},
  handleEventSelectionToggle,
  suppressEventMenu = false,
}) => {
  const [isMandotry, setIsMandatory] = useState(null);
  const dragStartedRef = useRef(false);

  const selectionDate = moment(date).format("YYYY-MM-DD");
  const eventMenuKey = `${item.id}_${selectionDate}`;
  const selectionKey = getEventShiftSelectionKey(item.id, selectionDate);
  const isEventSelected = isEventShiftSelected(
    selectedEvents,
    item.id,
    selectionDate,
  );

  const {
    openEventMenuKey,
    closeEventMenuAndFlyout,
    createEventMenuToggleHandler,
    handleMultipleDuplicatesClick,
    renderMultipleDuplicatesFlyout,
  } = useMultipleDuplicatesFlyout({
    menuStyles: styles,
  });

  const handleDuplicateEvent = useCallback(
    async (event, requestedDate, count = 1) => {
      await onDuplicateEvent(event, requestedDate, count);
      closeEventMenuAndFlyout();
    },
    [onDuplicateEvent, closeEventMenuAndFlyout],
  );

  let activeClass =
    type === constants.PopUp.innerPopUp && assignEmpModel?.index === index
      ? "active-event"
      : "";
  scrollClass = scrollClass ? scrollClass : "";
  let eventTags = convertTags(item?.eventTags);
  const showOfficeName = () => {
    return renderOfficeNameOrEnterprise(item, t);
  };
  const {
    profile,
    effectiveOwnerIdString,
    isOwner,
    isStaff,
    dataAccessValues,
  } = useAccountOwnerCommon();
  const createdByOthers = item?.isShift && profile?.id !== item?.createdById;

  const canEditAndViewOthersData = useCallback(
    (officeId) => {
      if (isStaff) {
        return (
          dataAccessValues?.officeList?.some(
            (res) => res.officeId === officeId && res.isAdmin,
          ) || false
        );
      }
      return isOwner;
    },
    [isStaff, isOwner, dataAccessValues?.officeList],
  );

  const canShowEventMenu =
    !suppressEventMenu &&
    canShowEventCardMenu(item, profile, {
      isOwner,
      canEditAndViewOthersData,
    });

  // Check if item can be dragged - with fallback if canDragEvent is not provided
  const canDrag = useMemo(() => {
    if (bulkSelectMode) return false;
    if (!canDragEvent || !item) return false;
    try {
      return canDragEvent(item);
    } catch (error) {
      console.error("Error checking canDragEvent:", error);
      return false;
    }
  }, [canDragEvent, item, bulkSelectMode]);

  const isDragged = isDragging && draggedEvent?.eventItem?.id === item.id;

  const handleDragStartLocal = (e) => {
    e.stopPropagation(); // Prevent event bubbling

    if (!canDrag) {
      e.preventDefault();
      return;
    }

    if (!handleDragStart) {
      console.warn("handleDragStart is not provided");
      e.preventDefault();
      return;
    }

    dragStartedRef.current = true;
    try {
      handleDragStart(e, item, date);
    } catch (error) {
      console.error("Error in handleDragStart:", error);
      dragStartedRef.current = false;
      e.preventDefault();
    }
  };

  const handleDragEndLocal = (e) => {
    dragStartedRef.current = false;
    if (handleDragEnd) {
      handleDragEnd(e);
    }
  };

  const selectionMeta = {
    id: item.id,
    date: moment(date).format("YYYY-MM-DD"),
    isDraft: item.isDraft,
    isShift: Boolean(item.isShift),
    publishAsEvent: item.publishAsEvent,
    userId: null,
    isUnassigned: isUnassignedSchedulerEvent(item),
  };

  const handleClick = (e) => {
    // Prevent click if drag was initiated
    if (dragStartedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      dragStartedRef.current = false;
      return;
    }
    if (isDragging) {
      e.stopPropagation();
      return;
    }
    if (bulkSelectMode) {
      handleEventSelectionToggle?.(selectionKey, selectionMeta);
      return;
    }
    moveToDetailPage(e, item.id, date, item);
  };

  return (
    <>
      <div
        className={`${styles["scheduler-event-box"]} ${
          activeClass && styles.active_event_box
        } ${styles[activeClass]} ${scrollClass}${
          bulkSelectMode ? ` ${styles["bulk-select-event-box"]}` : ""
        }`}
        style={{
          backgroundColor: item?.isDraft ? "#FEF3D7" : "",
          cursor: canDrag ? "grab" : "pointer",
          opacity: isDragged ? 0.5 : 1,
          userSelect: "none", // Prevent text selection during drag
          WebkitUserSelect: "none",
        }}
        draggable={canDrag}
        onDragStart={handleDragStartLocal}
        onDragEnd={handleDragEndLocal}
        onClick={handleClick}
        onDragOver={(e) => {
          // Prevent EventBox from capturing drag over events
          // Let the parent date cell handle it
          e.stopPropagation();
        }}
        onDrop={(e) => {
          // Prevent EventBox from capturing drop events
          // Let the parent date cell handle it
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {bulkSelectMode && (
          <div
            className={`ch-checkbox ${styles["bulk-select-checkbox"]}`}
            onClick={(e) => e.stopPropagation()}
          >
            <label>
              <input
                type="checkbox"
                checked={isEventSelected}
                onChange={() =>
                  handleEventSelectionToggle?.(selectionKey, selectionMeta)
                }
              />
              <span />
            </label>
          </div>
        )}
        <div className="d-flex justify-content-between position-relative">
          {(!viewSettingFilter.length || viewSettingFilter.includes("tag")) && (
            <div className="d-flex gap-1">
              {item?.isDraft && (
                <div
                  className={
                    styles["event-tag"] +
                    " " +
                    styles["draft-warning-bg"] +
                    (createdByOthers ? " m-0" : "")
                  }
                  draggable={false}
                  onDragStart={(e) => e.stopPropagation()}
                >
                  {t("scheduler.isDraft")}
                </div>
              )}

              {createdByOthers ? null : (
                <div
                  className={
                    styles["event-tag"] + " " + styles["theme-green-bg"]
                  }
                  draggable={false}
                  onDragStart={(e) => e.stopPropagation()}
                >
                  {item?.isShift
                    ? t("scheduler.agenda.shiftTag")
                    : t("scheduler.agenda.eventTag")}
                </div>
              )}
            </div>
          )}

          <div className="d-flex align-items-center gap-2">
            {item?.anyMissingFields && (
              <Fragment>
                <img
                  id={"mandatoryField"}
                  alt={"mandatoryField"}
                  src={AlertInfo}
                  width={25}
                  height={25}
                  onMouseOver={() => setIsMandatory(item?.id)}
                  onMouseLeave={() => setIsMandatory(null)}
                  draggable={false}
                  onDragStart={(e) => e.stopPropagation()}
                  className={styles["mandatory-field-icon"]}
                />
                <Tooltip
                  className="new-item-card-catalogue-tooltip"
                  isOpen={isMandotry === item?.id}
                  placement="top"
                  target={"mandatoryField"}
                >
                  {t("scheduler.mandatoryMsg")}
                </Tooltip>
              </Fragment>
            )}
            {canShowEventMenu && (
              <Dropdown
                isOpen={openEventMenuKey === eventMenuKey}
                toggle={createEventMenuToggleHandler(eventMenuKey)}
                onClick={(e) => e.stopPropagation()}
                onDragStart={(e) => e.stopPropagation()}
                className="interactive-dropdown"
              >
                <DropdownToggle
                  caret={false}
                  tag="div"
                  className="cursor-pointer"
                  draggable={false}
                  onDragStart={(e) => e.stopPropagation()}
                >
                  <div className={styles.ellipsis_icon}>
                    <ThreeDots />
                  </div>
                </DropdownToggle>
                <DropdownMenu
                  end
                  container="body"
                  className={styles["office-dropdown-menu"]}
                >
                  <DropdownItem
                    className={styles["office-dropdown-item"]}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicateEvent(item, date, 1);
                    }}
                  >
                    {t("scheduler.duplicateEvent", {
                      eventOrShiftOrLeave: item?.isShift
                        ? t("accountOwner.shiftTab")
                        : t("accountOwner.eventTab"),
                    })}
                  </DropdownItem>
                  <DropdownItem
                    className={styles["office-dropdown-item"]}
                    toggle={false}
                    onClick={handleMultipleDuplicatesClick(
                      eventMenuKey,
                      item,
                      date,
                    )}
                  >
                    {t("scheduler.multipleDuplicates")}
                  </DropdownItem>
                  <DropdownItem
                    className={styles["office-dropdown-item"]}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignUsers?.(item, date);
                    }}
                  >
                    {t("scheduler.assignStaff")}
                  </DropdownItem>
                  {/* <DropdownItem
                    className={styles["office-dropdown-item"]}
                    onClick={(e) => {
                      e.stopPropagation();
                      toast(
                        t("scheduler.actionComingSoon", {
                          action: t("scheduler.unassignUsers"),
                        }),
                      );
                    }}
                  >
                    {t("scheduler.unassignUsers")}
                  </DropdownItem> */}
                  <DropdownItem
                    className={styles["office-dropdown-item"]}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteEvent?.(
                        item,
                        moment(date).format("YYYY-MM-DD"),
                        {
                          lockSelectedUsers: false,
                          selectedUserIdsFromStaffView: [],
                        },
                        {
                          search: effectiveOwnerIdString
                            ? `?${effectiveOwnerIdString}`
                            : "",
                        },
                      );
                    }}
                  >
                    <span style={{ color: "#EE4F4F" }}>
                      {t("scheduler.deleteEvent", {
                        eventOrShift: item?.isShift
                          ? t("accountOwner.shiftTab")
                          : t("accountOwner.eventTab"),
                      })}
                    </span>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </div>
        </div>
        {createdByOthers &&
          (!viewSettingFilter.length || viewSettingFilter.includes("tag")) && (
            <div
              className={
                styles["event-tag"] +
                " " +
                styles["theme-green-bg"] +
                (createdByOthers ? " my-1" : "")
              }
              draggable={false}
              onDragStart={(e) => e.stopPropagation()}
            >
              {t("scheduler.agenda.shiftByOthers")}
            </div>
          )}
        {(!viewSettingFilter.length ||
          viewSettingFilter.includes("timeString")) && (
          <div
            className={styles["event-date"]}
            draggable={false}
            onDragStart={(e) => e.stopPropagation()}
          >
            {item?.isAllDayEvent
              ? t("scheduler.agenda.allDayEvent")
              : item?.startTime && item?.endTime
                ? moment(item?.startTime).format("h:mm A") +
                  " - " +
                  moment(item?.endTime).format("h:mm A")
                : ""}
            ({item?.timezoneCode})
          </div>
        )}
        {(!viewSettingFilter.length ||
          viewSettingFilter.includes("officeName")) && (
          <div
            className={styles["event-office"]}
            draggable={false}
            onDragStart={(e) => e.stopPropagation()}
          >
            {showOfficeName()}
          </div>
        )}

        {!viewSettingFilter.length && item?.location && (
          <div
            className={styles["event-office"] + " d-flex align-items-start"}
            draggable={false}
            onDragStart={(e) => e.stopPropagation()}
          >
            <img
              src={require("assets/images/pin_drop_black.svg").default}
              alt="caret"
              draggable={false}
              onDragStart={(e) => e.stopPropagation()}
            />
            <span className="ml-1 break_all ">{item.location}</span>
          </div>
        )}

        {(!viewSettingFilter.length || viewSettingFilter.includes("title")) &&
          (item?.isShift ? (
            viewSettingFilter.includes("title") && (
              <div
                className={styles["event-type"]}
                draggable={false}
                onDragStart={(e) => e.stopPropagation()}
              >
                {" "}
                {titleWordLimit(
                  item?.title?.trim()
                    ? item.title
                    : t("scheduler.agenda.shiftTag"),
                )}{" "}
              </div>
            )
          ) : (
            <div
              className={styles["event-type"]}
              draggable={false}
              onDragStart={(e) => e.stopPropagation()}
            >
              {" "}
              {titleWordLimit(item?.title)}{" "}
            </div>
          ))}

        <div>
          {(!viewSettingFilter.length ||
            viewSettingFilter.includes("assignedEmployees")) && (
            <div>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  item?.modifiedEmployeeCount > 1 &&
                    assignedEmployeesToggle(item, index, date, type);
                }}
                draggable={false}
                onDragStart={(e) => e.stopPropagation()}
              >
                {item?.modifiedEmployeeCount > 1 ||
                !item?.modifiedEmployeeCount ? (
                  <span className="link-btn">
                    {" "}
                    {`${item?.modifiedEmployeeCount || 0} Employees`}
                  </span>
                ) : (
                  <span className="link-btn-without-underline">
                    {" "}
                    {item?.eventEmployeeUsers}
                  </span>
                )}
              </span>
            </div>
          )}
          {(!viewSettingFilter.length ||
            viewSettingFilter.includes("requestTojoin")) &&
            item?.publishAsEvent && (
              <div>
                <span
                  className="link-btn interactive-element"
                  draggable={false}
                  onDragStart={(e) => e.stopPropagation()}
                >
                  {t("scheduler.reqToJoin")}:
                  {item?.eventRequestToJoinCount || 0}
                </span>
              </div>
            )}

          {eventTags?.length > 0 && (
            <div
              className={styles["event-meta-list"]}
              draggable={false}
              onDragStart={(e) => e.stopPropagation()}
            >
              {eventTags?.map((name, __index) => {
                return <span key={name + __index}> {name}</span>;
              })}
            </div>
          )}
        </div>
      </div>
      {renderMultipleDuplicatesFlyout(handleDuplicateEvent, t)}
    </>
  );
};
const DataMappingWithListing = ({
  highlightCheck,
  divRef,
  eventCardListing,
  list,
  assignEmpModel,
  showHolidayWhenBlinkingStopped,
  displayDate,
  dateShow,
  cellHeight,
  canAddShift,
  t,
  noteImage,
  index,
  holidayElements,
  handleEventList,
  moveToDetailPage,
  viewSettingFilter,
  assignedEmployeesToggle,
  setAssignEmpModel,
  setEventCardListing,
  classFOrHoliday,
  profile,
  history,
  canDragEvent,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDrop,
  isDragging,
  draggedEvent,
  dragPreviewDate,
  setDragPreviewDate,
  onDuplicateEvent,
  onAssignUsers,
  onDeleteEvent,
  bulkSelectMode,
  selectedEvents,
  handleEventSelectionToggle,
}) => {
  const cellRef = useRef({
    cell: null,
    cards: null,
    probe: null,
  });
  const availableHeightRef = useRef(0);
  const [cellWidthToken, setCellWidthToken] = useState(0);
  const [fitState, setFitState] = useState({
    signature: "",
    visibleCount: MIN_VISIBLE_MONTHLY_EVENTS,
  });

  const listing = useMemo(
    () => sortSchedulerEventsByStartTime(list?.schedulerEvents || []),
    [list?.schedulerEvents],
  );

  const showDragPreview = Boolean(
    isDragging &&
    draggedEvent?.eventItem &&
    dragPreviewDate &&
    moment(displayDate).format("YYYY-MM-DD") === dragPreviewDate,
  );

  /**
   * Everything that can change either the height of a single card (display
   * settings, cell width) or the set of matching events invalidates a previous
   * fit calculation and restarts it from the minimum.
   */
  const fitSignature = useMemo(
    () =>
      [
        cellHeight,
        cellWidthToken,
        showDragPreview ? "drag" : "",
        viewSettingFilter?.join("|") ?? "",
        listing.map((event) => event.id).join(","),
      ].join("::"),
    [cellHeight, cellWidthToken, showDragPreview, viewSettingFilter, listing],
  );

  const maxVisibleEvents =
    fitState.signature === fitSignature
      ? fitState.visibleCount
      : MIN_VISIBLE_MONTHLY_EVENTS;
  const hiddenEventsCount = Math.max(0, listing.length - maxVisibleEvents);

  /**
   * Reveals one more card per layout pass until the next card no longer fits in
   * the room the cell actually has. Measuring inside a layout effect (instead of
   * a polling timer) keeps every cell of a week in sync and makes the result
   * deterministic, so shorter cards - for example when only "Title" is enabled
   * in Display Settings - immediately surface more of the matching events.
   */
  useLayoutEffect(() => {
    const cellElement = cellRef.current.cell;
    const cardsElement = cellRef.current.cards;
    if (!cellElement || !cardsElement) return;

    if (maxVisibleEvents === MIN_VISIBLE_MONTHLY_EVENTS) {
      availableHeightRef.current = getAvailableCardsHeight(
        cellElement,
        cardsElement,
      );
    }

    const probeElement = cellRef.current.probe;
    if (!hiddenEventsCount || !probeElement) return;

    const nextCardHeight = probeElement.offsetHeight;
    if (!nextCardHeight) return;

    const fits =
      cardsElement.offsetHeight + nextCardHeight <=
      availableHeightRef.current + MONTHLY_FIT_TOLERANCE;
    if (fits) {
      setFitState({
        signature: fitSignature,
        visibleCount: maxVisibleEvents + 1,
      });
    }
  }, [fitSignature, maxVisibleEvents, hiddenEventsCount]);

  // Card heights depend on the cell width, so a responsive change has to redo
  // the fit calculation from scratch.
  useEffect(() => {
    const cellElement = cellRef.current.cell;
    if (!cellElement || typeof ResizeObserver === "undefined") return;

    let lastWidth = cellElement.getBoundingClientRect().width;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? lastWidth;
      if (Math.abs(width - lastWidth) < 1) return;
      lastWidth = width;
      setCellWidthToken((token) => token + 1);
    });
    observer.observe(cellElement);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={(ref) => {
        cellRef.current.cell = ref;
        if (highlightCheck) {
          divRef.current = ref;
        }
      }}
      style={{ minHeight: cellHeight }}
      className={`${styles["monthly-col"]} ${styles["monthly-td"]}
                            ${
                              (eventCardListing?.date === list?.date ||
                                assignEmpModel?.date === list?.date) &&
                              styles["active-date"]
                            } ${showHolidayWhenBlinkingStopped}`}
      key={displayDate}
      onDragOver={(e) => {
        if (handleDragOver) {
          handleDragOver(e, displayDate);
        }
      }}
      onDragLeave={(e) => {
        // Only clear preview if leaving the cell entirely (not just moving to a child)
        const relatedTarget = e.relatedTarget;
        if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
          setDragPreviewDate(null);
        }
      }}
      onDrop={(e) => {
        if (handleDrop) {
          // Ensure displayDate is formatted correctly
          let dateToPass = displayDate;
          if (displayDate instanceof Date) {
            dateToPass = moment(displayDate).format("YYYY-MM-DD");
          } else if (displayDate && typeof displayDate.format === "function") {
            // It's a moment object
            dateToPass = displayDate.format("YYYY-MM-DD");
          } else if (typeof displayDate === "string") {
            // Already a string, validate it
            const parsed = moment(displayDate);
            dateToPass = parsed.isValid()
              ? parsed.format("YYYY-MM-DD")
              : displayDate;
          } else {
            // Try to parse
            const parsed = moment(displayDate);
            dateToPass = parsed.isValid()
              ? parsed.format("YYYY-MM-DD")
              : moment(displayDate).format("YYYY-MM-DD");
          }
          handleDrop(e, dateToPass);
        }
      }}
    >
      <div className={styles["date-box"]}>
        {list?.note && <NoteView list={list} dateShow={dateShow} />}
        {dateShow}
        <span className={styles["mobile-week-days"]}>
          ({getWeekDay(displayDate)})
        </span>
      </div>

      <UncontrolledDropdown className={styles.ellipsis_position}>
        <DropdownToggle caret={false} tag="div" className="cursor-pointer">
          <div className={styles.ellipsis_icon + " " + `ellipsis-icon`}>
            <ThreeDots />
          </div>
        </DropdownToggle>
        <DropdownMenu end className={styles["office-dropdown-menu"]}>
          <DropdownItem className={styles["office-dropdown-item"]}>
            <Link
              to={{
                pathname: constants.routes.scheduler.addEvent,
                state: {
                  date: moment(displayDate).format("YYYY-MM-DD"),
                },
              }}
              className={styles["add-event-btn"]}
            >
              {t(
                canAddShift
                  ? "accountOwner.newEventShift"
                  : "accountOwner.newEvent",
              )}
            </Link>
          </DropdownItem>
          <DropdownItem className={styles["office-dropdown-item"]}>
            {noteImage(list, index)}
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
      {holidayElements}
      {/* Drag Preview Card */}
      {showDragPreview && (
        <div
          className={styles["scheduler-event-box"]}
          style={{
            backgroundColor: "#C8E36433",
            border: "2px dashed #C8E364",
            borderRadius: "6px",
            opacity: 0.6,
            pointerEvents: "none",
            marginBottom: "4px",
          }}
        >
          <div className="d-flex justify-content-between">
            <div className="d-flex gap-1">
              {draggedEvent.eventItem?.isDraft && (
                <div
                  className={
                    styles["event-tag"] + " " + styles["draft-warning-bg"]
                  }
                  style={{ opacity: 0.6 }}
                >
                  {t("scheduler.isDraft")}
                </div>
              )}
              {!(
                draggedEvent.eventItem?.isShift &&
                profile?.id !== draggedEvent.eventItem?.createdById
              ) && (
                <div
                  className={
                    styles["event-tag"] + " " + styles["theme-green-bg"]
                  }
                  style={{ opacity: 0.6 }}
                >
                  {draggedEvent.eventItem?.isShift
                    ? t("scheduler.agenda.shiftTag")
                    : t("scheduler.agenda.eventTag")}
                </div>
              )}
            </div>
            <div className="d-flex align-items-center">
              <div className={styles.ellipsis_icon} style={{ opacity: 0.6 }}>
                <ThreeDots />
              </div>
            </div>
          </div>
          {draggedEvent.eventItem?.isShift &&
            profile?.id !== draggedEvent.eventItem?.createdById && (
              <div
                className={
                  styles["event-tag"] + " " + styles["theme-green-bg"] + " my-1"
                }
                style={{ opacity: 0.6 }}
              >
                {t("scheduler.agenda.shiftByOthers")}
              </div>
            )}
          {(!viewSettingFilter.length ||
            viewSettingFilter.includes("timeString")) && (
            <div className={styles["event-date"]} style={{ opacity: 0.6 }}>
              {draggedEvent.eventItem?.isAllDayEvent
                ? t("scheduler.agenda.allDayEvent")
                : draggedEvent.eventItem?.startTime &&
                    draggedEvent.eventItem?.endTime
                  ? moment(draggedEvent.eventItem?.startTime).format("h:mm A") +
                    " - " +
                    moment(draggedEvent.eventItem?.endTime).format("h:mm A")
                  : ""}
              {draggedEvent.eventItem?.timezoneCode &&
                ` (${draggedEvent.eventItem?.timezoneCode})`}
            </div>
          )}
          {(!viewSettingFilter.length ||
            viewSettingFilter.includes("officeName")) && (
            <div className={styles["event-office"]} style={{ opacity: 0.6 }}>
              {renderOfficeNameOrEnterprise(draggedEvent.eventItem, t)}
            </div>
          )}
          <div
            className={styles["event-title"]}
            style={{ opacity: 0.6 }}
            title={draggedEvent.eventItem?.title}
          >
            {titleWordLimit(draggedEvent.eventItem?.title, 20)}
          </div>
        </div>
      )}
      <div
        className={styles["monthly-event-slot"]}
        ref={(ref) => {
          cellRef.current.cards = ref;
        }}
      >
        {listing?.slice(0, maxVisibleEvents).map((item, eventIndex) => {
          return (
            <Fragment key={displayDate + "frag2" + eventIndex}>
              <EventBox
                item={item}
                index={eventIndex}
                date={displayDate}
                type={constants.PopUp.outerPopUp}
                parentIndex={index}
                holidayElements={holidayElements}
                moveToDetailPage={moveToDetailPage}
                viewSettingFilter={viewSettingFilter}
                assignEmpModel={assignEmpModel}
                t={t}
                assignedEmployeesToggle={assignedEmployeesToggle}
                onDuplicateEvent={onDuplicateEvent}
                onAssignUsers={onAssignUsers}
                onDeleteEvent={onDeleteEvent}
                history={history}
                canDragEvent={canDragEvent}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                isDragging={isDragging}
                draggedEvent={draggedEvent}
                bulkSelectMode={bulkSelectMode}
                selectedEvents={selectedEvents}
                handleEventSelectionToggle={handleEventSelectionToggle}
              />

              <div className={styles["more-event-box"]}>
                {eventIndex === maxVisibleEvents - 1 && (
                  <SeeMoreButton
                    events={listing}
                    hiddenEventsCount={hiddenEventsCount}
                    index={eventIndex}
                    date={displayDate}
                    handleEventList={handleEventList}
                    t={t}
                  />
                )}
                {eventCardListing &&
                  list?.date &&
                  eventCardListing?.index === eventIndex &&
                  eventCardListing?.date === list?.date && (
                    <EventCard
                      index={index}
                      parentIndex={index}
                      list={listing?.map((res) => res)}
                      date={dateShow}
                      assignEmpModel={assignEmpModel}
                      eventCardListing={eventCardListing}
                      setEventCardListing={setEventCardListing}
                      setAssignEmpModel={setAssignEmpModel}
                      t={t}
                      holidayElements={holidayElements}
                      moveToDetailPage={moveToDetailPage}
                      viewSettingFilter={viewSettingFilter}
                      maxVisibleEvents={maxVisibleEvents}
                      assignedEmployeesToggle={assignedEmployeesToggle}
                      classFOrHoliday={classFOrHoliday}
                      history={history}
                      canDragEvent={canDragEvent}
                      handleDragStart={handleDragStart}
                      handleDragEnd={handleDragEnd}
                      isDragging={isDragging}
                      draggedEvent={draggedEvent}
                      onDuplicateEvent={onDuplicateEvent}
                      onAssignUsers={onAssignUsers}
                      onDeleteEvent={onDeleteEvent}
                      bulkSelectMode={bulkSelectMode}
                      selectedEvents={selectedEvents}
                      handleEventSelectionToggle={handleEventSelectionToggle}
                    />
                  )}
                {constants.PopUp.outerPopUp === assignEmpModel?.type &&
                  assignEmpModel?.index === eventIndex &&
                  assignEmpModel?.date === list?.date && (
                    <EventEmployee
                      t={t}
                      setAssignEmpModel={setAssignEmpModel}
                      assignEmpModel={assignEmpModel}
                    />
                  )}
              </div>
            </Fragment>
          );
        })}
        {/*
          Hidden probe for the next candidate card. It is absolutely positioned
          inside the cards container so it is laid out at the exact width a real
          card gets - measuring it at any other width would mis-predict line
          wrapping and therefore the number of cards that fit.
        */}
        {hiddenEventsCount > 0 && (
          <div
            className={styles["monthly-event-probe"]}
            aria-hidden="true"
            ref={(ref) => {
              cellRef.current.probe = ref;
            }}
          >
            {[listing[maxVisibleEvents]].map((item, eventIndex) => (
              <EventBox
                key={displayDate + "probe" + item.id}
                item={item}
                index={eventIndex}
                date={displayDate}
                type={constants.PopUp.outerPopUp}
                parentIndex={index}
                holidayElements={holidayElements}
                moveToDetailPage={moveToDetailPage}
                viewSettingFilter={viewSettingFilter}
                assignEmpModel={assignEmpModel}
                t={t}
                assignedEmployeesToggle={assignedEmployeesToggle}
                onDuplicateEvent={onDuplicateEvent}
                onAssignUsers={onAssignUsers}
                onDeleteEvent={onDeleteEvent}
                history={history}
                canDragEvent={canDragEvent}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                isDragging={isDragging}
                draggedEvent={draggedEvent}
                bulkSelectMode={bulkSelectMode}
                selectedEvents={selectedEvents}
                handleEventSelectionToggle={handleEventSelectionToggle}
                suppressEventMenu
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
export const NoteView = ({ list, dateShow }) => {
  // Use a stable, DOM-safe id per instance (unique per note/list or dateShow)
  const rawId = list?.id ?? String(dateShow).replace(/[^a-zA-Z0-9-_]/g, "-");
  const targetId = "added-note-" + rawId;
  return (
    <>
      <img
        src={AddedNote}
        alt="addedNote"
        className={styles["addedNote"]}
        id={targetId}
      />
      <UncontrolledTooltip
        className={styles["new-item-card-catalogue-tooltip"]}
        placement="top"
        target={targetId}
      >
        <Text size="10px" weight="700" color="#ffffff">
          {list?.data?.text ?? ""}
        </Text>
      </UncontrolledTooltip>
    </>
  );
};
function EventsShiftsMonthly({
  t,
  settotalEvents,
  currentDate,
  selectedOfficeFilter,
  searchValue,
  upDateListonApply,
  viewSettingFilter,
  canAddShift,
  eventShiftFilter = [],
  isEnterpriseFilterActive = false,
  appliedPublishedDraftFilter = [],
  selectedEmpFilter = [],
  holidaysObject,
  onVisibleDraftEventsUpdate,
  onVisibleClearableItemsUpdate,
  bulkSelectMode,
  selectedEvents,
  handleEventSelectionToggle,
  handleSelectableEventsUpdate,
}) {
  const {
    effectiveOwnerId: selectedOwnerId,
    profile,
    effectiveOwnerIdString,
    isStaff,
  } = useAccountOwnerCommon();
  const [monthDates, setmonthDates] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [noteEventListing, setEventListing] = useState([]);
  const [noteEventListingFilter, setEventListingFilter] = useState([]);
  const [eventCardListing, setEventCardListing] = useState({});

  const [assignEmpModel, setAssignEmpModel] = useState({});
  const [isLoader, setIsLoading] = useState(false);
  const [notesIndex, setNotesIndex] = useState();
  const history = useHistory();
  const [isAddNotesModal, setIsAddNotesModal] = useState({
    visible: false,
    date: "",
    value: "",
  });
  const [isEditNotesModal, setIsEditNotesModal] = useState({ visible: false });
  const [deleteConfirmation, setdeleteConfirmation] = useState({
    visible: false,
    id: "",
  });

  // Drag and drop state
  const [draggedEvent, setDraggedEvent] = useState(null);
  const draggedEventDateRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [editModalConfirmation, setEditModalConfirmation] = useState(false);
  const [pendingDragUpdate, setPendingDragUpdate] = useState(null);
  const [isDragDropLoading, setIsDragDropLoading] = useState(false);
  const [dragPreviewDate, setDragPreviewDate] = useState(null); // Track which date cell is being hovered

  // Use ref to store dragged event for immediate access (state updates are async)
  const draggedEventRef = useRef(null);

  const createSchedulerEventMutation = useCreateUpdateSchedulerEventMutation();

  const [tableHeight, setTableHeight] = useState("auto");
  const [gridHeight, setGridHeight] = useState(0);
  const [columnsPerRow, setColumnsPerRow] = useState(DAYS_PER_WEEK);
  const scrollContainerRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRowRef = useRef(null);
  const roomInsideCardRef = useRef(-1);
  const isMeasuringRoomRef = useRef(false);
  const isHighlightingRef = useRef(false);
  const lastHighlightedRef = useRef(null);

  useEffect(() => {
    /**
     * @param {{ remeasureRoom?: boolean }} [options] `remeasureRoom` refreshes
     *   the card cap and the height handed to the date cells. Both are skipped
     *   while scrolling: re-measuring means briefly collapsing the calendar,
     *   which would fight the scroll, and resizing the cells mid-scroll would
     *   make events appear and disappear under the cursor.
     */
    const calculateDynamicHeight = ({ remeasureRoom = false } = {}) => {
      if (isHighlightingRef.current === true) return;

      const container = scrollContainerRef.current;
      if (!container) return;

      // `< 0` means never measured. A measured `0` must not retry, or every
      // scroll event would collapse the calendar looking for a card that is
      // not there.
      if (remeasureRoom || roomInsideCardRef.current < 0) {
        isMeasuringRoomRef.current = true;
        roomInsideCardRef.current = measureRoomInsideCard(container);
        isMeasuringRoomRef.current = false;
      }
      const room = roomInsideCardRef.current;

      // Height that puts the bottom of the calendar on the bottom edge of the
      // screen. `top` is deliberately not clamped: it goes negative once the
      // calendar has scrolled up past the top of the viewport, and that is what
      // lets the calendar keep reaching the bottom edge instead of ending short
      // of it. It cannot run away because `room` below is the ceiling.
      const topOffset = container.getBoundingClientRect().top;
      const onScreenHeight =
        window.innerHeight - topOffset - MONTHLY_BOTTOM_GAP;

      // An implausibly small measurement means the card is not sized by the
      // sidebar at all - it is hidden or floating, as it is on small screens -
      // so there is nothing to stay clear of and the calendar falls back to
      // filling the viewport.
      const cap = room >= MONTHLY_MIN_ROOM ? room : 0;

      // That room is otherwise the hard limit: growing into it is what makes the
      // calendar reach the bottom of the card, and stopping there is what keeps
      // it from stretching the card. Scrolling brings more of the calendar on
      // screen, which is what lets it grow towards the limit. The floor gives
      // way to the limit too, so that a short card is met exactly rather than
      // overshot.
      const limit = cap ? Math.min(onScreenHeight, cap) : onScreenHeight;
      const floor = cap
        ? Math.min(MONTHLY_MIN_TABLE_HEIGHT, cap)
        : MONTHLY_MIN_TABLE_HEIGHT;
      const computedHeight = Math.round(Math.max(floor, limit));

      setTableHeight(`${computedHeight}px`);

      if (remeasureRoom) {
        // Date cells split the room inside the card rather than the height
        // above: that keeps them the same size while the container grows on
        // scroll, and it makes the weeks add up to the full room so the grid
        // reaches the bottom of the card instead of leaving a gap under it.
        const cellRoom = cap || onScreenHeight;
        setGridHeight(
          Math.max(0, cellRoom - (headerRef.current?.offsetHeight ?? 0)),
        );
        setColumnsPerRow(getColumnsPerRow(bodyRowRef.current));
      }
    };

    const remeasure = () => calculateDynamicHeight({ remeasureRoom: true });
    const refit = () => calculateDynamicHeight();

    remeasure();
    const timeoutId = setTimeout(remeasure, 300);

    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", refit, { passive: true });

    // The sidebar is what the wrapper is normally as tall as, so opening it or
    // expanding a filter section changes how much room the calendar has. Only
    // the inner element is observed: its height comes from the filters alone,
    // never from the calendar, so this cannot feed back into itself.
    const sidebar = scrollContainerRef.current
      ?.closest(".scheduler-tabs-main-wrapper")
      ?.querySelector(".scheduler-sidebar");
    const sidebarObserver =
      sidebar && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            if (isMeasuringRoomRef.current) return;
            remeasure();
          })
        : null;
    sidebarObserver?.observe(sidebar);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", refit);
      sidebarObserver?.disconnect();
    };
  }, [noteEventListing]);
  // --- END OF DYNAMIC HEIGHT CALCULATION ---

  const {
    isLoading,
    data: eventListing,
    error,
    refetch,
    isFetching,
    isRefetching: isRefetchingMonthly,
  } = useGetOfficeEventMonthly(
    selectedOwnerId,
    dateRange?.startDate || "",
    dateRange?.endDate || "",
    selectedOfficeFilter,
    selectedEmpFilter,
    {
      enabled: !!selectedOwnerId && !!dateRange?.startDate,
      refetchOnMount: true,
    },
  );

  const {
    isLoading: notesLoad,
    data: notesListing,
    error: isError,
    refetch: fetchNotes,
  } = useGetNotesEventMonthly(
    selectedOwnerId,
    dateRange?.startDate || "",
    dateRange?.endDate || "",
    {
      enabled: !!selectedOwnerId && !!dateRange?.startDate,
      refetchOnMount: true,
    },
  );

  // Handle error for notes and events listing
  useEffect(() => {
    if (error || isError) {
      toast.error(error?.message || isError?.message || t("CustomErrorMsg"));
    }
  }, [error, isError]);

  useEffect(() => {
    if (currentDate) {
      calanderDates(currentDate);
      const startDate = moment(currentDate)
        .startOf("month")
        .startOf("isoweek")
        .format("YYYY-MM-DD");
      const endDate = moment(currentDate).endOf("month").format("YYYY-MM-DD");

      setTimeout(() => {
        setDateRange({ startDate, endDate });
      }, 50);
    }
  }, [currentDate]);
  const loadingRef = useRef(null);
  useEffect(() => {
    if (isLoading || isFetching || isRefetchingMonthly) {
      return;
    }
    if (dateRange?.startDate && selectedOwnerId) {
      // Clear existing data first
      setEventListing([]);
      setEventListingFilter([]);
      if (loadingRef.current) {
        clearTimeout(loadingRef.current);
      }

      // Then refetch with a slight delay
      loadingRef.current = setTimeout(() => {
        refetch();
        fetchNotes();
      }, 50);
    }
  }, [selectedOwnerId, selectedOfficeFilter, upDateListonApply]);

  useEffect(() => {
    if (!isLoading && !notesLoad && eventListing?.data) {
      try {
        // Clear any existing timeouts to prevent race conditions
        if (window.dataProcessingTimeout) {
          clearTimeout(window.dataProcessingTimeout);
        }

        window.dataProcessingTimeout = setTimeout(() => {
          const eventsData = eventListing?.data || [];
          const notesData = notesListing?.data || [];

          // Create clean copy of event data
          let combineData = JSON.parse(JSON.stringify(eventsData));

          // Process notes properly
          if (notesData.length) {
            combineData = combineData.map((date) => {
              for (const note of notesData) {
                if (dateFormater(date.date) === dateFormater(note.date)) {
                  date.note = true;
                  date.data = note;
                  break;
                }
              }
              return date;
            });
          }

          // Calculate event count correctly
          let eventCount = 0;
          for (const item of combineData) {
            eventCount += item?.schedulerEvents?.length || 0;
          }

          // Update states in correct order
          settotalEvents(eventCount);

          // Ensure unique entries
          const uniqueData = uniq(combineData, "id");

          // Update both state variables with fresh arrays
          setEventListing([...uniqueData]);
          setEventListingFilter([...uniqueData]);
        }, 50);
      } catch (err) {
        console.error("Error processing event data:", err);
      }
    }
  }, [eventListing, notesListing, isLoading, notesLoad, isRefetchingMonthly]);

  useEffect(() => {
    if (currentDate && dateRange?.startDate && selectedOwnerId) {
      // Clear existing data
      setEventListing([]);
      setEventListingFilter([]);

      // Force refetch with slight delay
      setTimeout(() => {
        refetch();
        fetchNotes();
      }, 100);
    }
  }, []);

  useEffect(() => {
    setEventListing([...noteEventListing]);
  }, [viewSettingFilter]);

  const dateFormater = (date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  const calanderDates = (dateCurrent) => {
    let dates = generateCalanderMonthViewEvent(dateCurrent);
    dates && setmonthDates([...dates]);
  };

  const handleEventList = useCallback((list, index, date) => {
    try {
      let dataList = JSON.parse(JSON.stringify(list));
      dataList.splice(0, 1);
      setAssignEmpModel({});
      setEventCardListing({
        list: uniq(dataList, "id"),
        index,
        date,
      });
    } catch (err) {
      handleError(err);
    }
  }, []);

  const assignedEmployeesToggle = useCallback(
    async (list, index, date, type) => {
      try {
        setIsLoading(true);
        setAssignEmpModel({});
        // Occurrence calendar day from ListForAssigner (outer `date`), not event.createdAt / series start
        const schedularEventDate = moment(date).format("YYYY-MM-DD");

        const res = await getAssignedEmployess(
          list.id,
          schedularEventDate,
          selectedEmpFilter,
        );
        if (res && res?.data?.length) {
          setAssignEmpModel({
            list: res.data,
            index: index,
            date: date,
            type: type,
          });
        }
      } catch (err) {
        handleError(err);
      }
      setIsLoading(false);
    },
    [],
  );

  const onDuplicateEvent = useCallback(
    async (eventItem, requestedDate, duplicateCount = 1) => {
      try {
        setIsLoading(true);
        await duplicateSchedulerEvent(
          buildDuplicateSchedulerEventPayload({
            schedulerEventId: eventItem?.id,
            eventDate: requestedDate,
            duplicateCount,
          }),
        );
        toast.success(t("scheduler.duplicateSuccess"));
        refetch();
        fetchNotes();
      } catch (error) {
        toast.error(error?.message || t("CustomErrorMsg"));
      } finally {
        setIsLoading(false);
      }
    },
    [fetchNotes, refetch, t],
  );

  const { handleCardDeleteClick, renderSimpleDeleteConfirmModal } =
    useSimpleEventCardDelete({
      t,
      history,
      effectiveOwnerIdString,
      onSuccess: async () => {
        await refetch();
        await fetchNotes();
      },
    });

  const { isAssignLoading, openAssignUsersModal, renderAssignUsersModals } =
    useAssignUsersFromCard({
      t,
      ownerId: selectedOwnerId,
      canAddShift,
      isStaff,
      onSuccess: async () => {
        await refetch();
        await fetchNotes();
      },
    });

  const editNotesModelShow = (data) => {
    isEditNotesModal.visible = true;
    isEditNotesModal.data = data;
    setIsEditNotesModal({ ...isEditNotesModal });
  };

  const noteImage = (list, index) => {
    if (list?.note) {
      return (
        <span
          className={`${styles["notes-icon"]} pointer`}
          title={t("accountOwner.viewNote")}
          onClick={() => {
            setNotesIndex(index);
            editNotesModelShow(list);
          }}
        >
          {t("accountOwner.viewNote")}
        </span>
      );
    } else {
      return (
        <span
          className={`${styles["notes-icon"]} pointer`}
          title={t("staff.addNote")}
          onClick={() => {
            setNotesIndex(index);
            addNotesModelShow(list, "ADD");
          }}
        >
          {t("staff.addNote")}
        </span>
      );
    }
  };

  const addNotesModelShow = (data, type) => {
    isAddNotesModal.visible = true;
    isAddNotesModal.data = data;
    isAddNotesModal.type = type;
    if (type === "EDIT") {
      isAddNotesModal.value = data.data.text;
    }
    setIsAddNotesModal({ ...isAddNotesModal });
  };

  const updateList = (data, type) => {
    if (data && (type === "ADD" || type === "EDIT")) {
      noteEventListing[notesIndex].note = true;
      noteEventListing[notesIndex].data = data.data;
    }
    if (type === "DELETE") {
      noteEventListing[notesIndex].note = false;
      delete noteEventListing[notesIndex].data;
    }
    setEventListing([...noteEventListing]);
    setEventListingFilter([...noteEventListing]);
  };

  const addModal = useMemo(() => {
    return (
      <AddNotesModal
        isAddNotesModal={isAddNotesModal}
        setIsAddNotesModal={setIsAddNotesModal}
        updateList={updateList}
        setIsLoading={setIsLoading}
        getDetailsOwnerIfStaff={selectedOwnerId}
      />
    );
    // eslint-disable-next-line
  }, [isAddNotesModal]);

  const editModal = useMemo(() => {
    return (
      <EditNotesModal
        isEditNotesModal={isEditNotesModal}
        setIsEditNotesModal={setIsEditNotesModal}
        addNotesModelShow={addNotesModelShow}
        setdeleteConfirmation={setdeleteConfirmation}
      />
    );
    // eslint-disable-next-line
  }, [isEditNotesModal]);
  const moveToDetailPage = useCallback(
    (e, id, date) => {
      if (e.target.closest(".interactive-element")) {
        return;
      }
      history.push({
        pathname: constants.routes.scheduler.eventDetails.replace(
          ":eventId",
          encodeId(id),
        ),
        state: { requestedDate: date },
        search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
      });
    },
    [history, effectiveOwnerIdString],
  );

  // Check if event can be dragged
  const canDragEvent = useCallback(
    (eventItem) => {
      const createdByMe = eventItem.createdById === profile?.id;
      const ownedByMe =
        eventItem.ownerId === selectedOwnerId ||
        eventItem.ownerId === profile?.id;
      const createdByOthers =
        eventItem?.isShift && profile?.id !== eventItem?.createdById;

      // Check for shifts BEFORE checking for regular events
      if (eventItem?.isShift) {
        if (createdByMe) {
          return true;
        }
        // For shifts created by others, check if user is owner or admin
        if (createdByOthers) {
          const isAdmin = profile?.isAdmin;
          const isAccountOwner =
            profile && profile?.role
              ? profile.role.systemRole === constants.systemRoles.accountOwner
              : false;
          if (isAccountOwner || isAdmin) {
            return true;
          }
        }
        return false;
      }

      // Regular events (non-shifts) can only be moved by creator
      if (
        eventItem.agendaType === constants.agendaType.EVENT ||
        !eventItem.agendaType
      ) {
        return createdByMe;
      }

      return false;
    },
    [profile?.id, selectedOwnerId],
  );

  // Calculate new date for monthly view (preserving time)
  const calculateNewDateForMonthly = useCallback(
    (eventItem, targetDateString) => {
      if (!eventItem || !targetDateString) {
        return null;
      }

      const newDate = moment(targetDateString).startOf("day");

      // Get original date
      let originalDate = null;
      if (eventItem.date) {
        originalDate = moment(eventItem.date).startOf("day");
      } else if (eventItem.startTime) {
        originalDate = moment(eventItem.startTime).startOf("day");
      }

      // Check if same date
      if (originalDate && newDate.isSame(originalDate, "day")) {
        return null;
      }

      // Preserve original time
      let newStartTime, newEndTime;
      if (
        eventItem.isAllDayEvent ||
        !eventItem.startTime ||
        !eventItem.endTime
      ) {
        // All-day event
        newStartTime = newDate.startOf("day").toDate();
        newEndTime = newDate.endOf("day").toDate();
      } else {
        // Preserve time from original event
        const originalStart = moment(eventItem.startTime);
        const originalEnd = moment(eventItem.endTime);
        const originalHour = originalStart.hour();
        const originalMinute = originalStart.minute();
        const originalEndHour = originalEnd.hour();
        const originalEndMinute = originalEnd.minute();

        newStartTime = newDate
          .hour(originalHour)
          .minute(originalMinute)
          .second(0)
          .toDate();
        newEndTime = newDate
          .hour(originalEndHour)
          .minute(originalEndMinute)
          .second(0)
          .toDate();
      }

      return {
        newDate: newDate.toDate(),
        newStartTime: newStartTime,
        newEndTime: newEndTime,
        newStartTimeString: moment(newStartTime).format("YYYY-MM-DDTHH:mm"),
        newEndTimeString: moment(newEndTime).format("YYYY-MM-DDTHH:mm"),
      };
    },
    [],
  );

  // Helper to check if time changed
  const getTimeCheck = useCallback((alreadyTime, selectedTime) => {
    if (alreadyTime) {
      const selectedStartTime = moment(selectedTime);
      const alreadySelectedStartTime = moment(alreadyTime);
      if (
        selectedStartTime.hour() !== alreadySelectedStartTime.hour() ||
        selectedStartTime.minute() !== alreadySelectedStartTime.minute()
      ) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, []);

  // Internal function to perform the actual update
  const performEventUpdate = useCallback(
    async (eventData, newDateTime, editType, originalRequestedDate = null) => {
      if (!newDateTime || !eventData) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      const requestedDateForCheck = originalRequestedDate
        ? originalRequestedDate
        : eventData.requestedDate
          ? moment(eventData.requestedDate).format("YYYY-MM-DD")
          : eventData.startTime
            ? moment(eventData.startTime).format("YYYY-MM-DD")
            : moment(eventData.date || newDateTime.newDate).format(
                "YYYY-MM-DD",
              );

      const dateChangedResult = matchedBothDatesValues({
        eventDetail: eventData,
        repeatedEndDate: eventData.repeatedEndDate
          ? moment(eventData.repeatedEndDate).toDate()
          : moment().add(3, "month").toDate(),
        requestedDate: requestedDateForCheck,
        eventDate: newDateTime.newDate,
      });

      const startTimeChanged = getTimeCheck(
        eventData.startTime,
        newDateTime.newStartTimeString,
      );
      const endTimeChanged = getTimeCheck(
        eventData.endTime,
        newDateTime.newEndTimeString,
      );

      const originalRequestedDateForParams = originalRequestedDate
        ? moment(originalRequestedDate).format("YYYY-MM-DDTHH:mm")
        : eventData.requestedDate
          ? moment(eventData.requestedDate).format("YYYY-MM-DDTHH:mm")
          : eventData.startTime
            ? moment(eventData.startTime).format("YYYY-MM-DDTHH:mm")
            : moment(newDateTime.newDate).format("YYYY-MM-DDTHH:mm");

      const requestedDateForDateCalc = originalRequestedDate
        ? moment(originalRequestedDate).toDate()
        : eventData.requestedDate
          ? moment(eventData.requestedDate).toDate()
          : eventData.startTime
            ? moment(eventData.startTime).toDate()
            : null;

      const eventDate = moment(newDateTime.newDate).toDate();

      // Check if the date actually changed (comparing dates only, ignoring time)
      const originalDateForComparison = requestedDateForDateCalc
        ? moment(requestedDateForDateCalc).startOf("day")
        : eventData.startTime
          ? moment(eventData.startTime).startOf("day")
          : eventData.date
            ? moment(eventData.date).startOf("day")
            : null;

      const newDateForComparison = moment(eventDate).startOf("day");
      const dateActuallyChanged = originalDateForComparison
        ? !originalDateForComparison.isSame(newDateForComparison, "day")
        : dateChangedResult.startDateChanged;

      // For editType 2 (thisAndFutureEvent) or 3 (allEvent), if only time changed (same date),
      // IsEventDateChange should be false
      const finalIsEventDateChange =
        (editType === editTypesVariables.thisAndFutureEvent ||
          editType === editTypesVariables.allEvent) &&
        !dateActuallyChanged
          ? false
          : dateActuallyChanged;

      const params = {
        SchedulerEventId: eventData.id,
        OwnerId: selectedOwnerId,
        Title: eventData.title,
        Date: moment(
          requestedDateForDateCalc &&
            moment(requestedDateForDateCalc)
              .startOf("day")
              .isSame(moment(eventDate).startOf("day"))
            ? eventData?.date
            : eventDate,
        ).format("YYYY-MM-DDTHH:mm"),
        StartTime: newDateTime.newStartTimeString,
        EndTime: newDateTime.newEndTimeString,
        IsAllDayEvent: eventData.isAllDayEvent || false,
        RepeatedType: eventData.repeatedType || 1,
        RepeatedDay: moment(newDateTime.newDate).isoWeekday(),
        RepeatedEndDate:
          eventData.repeatedType > 1 && eventData.repeatedEndDate
            ? moment(eventData.repeatedEndDate).format("YYYY-MM-DD")
            : null,
        Location: eventData.location || "",
        BlockForPatient: eventData.blockForPatient || false,
        Note: eventData.note || "",
        PublishAsEvent: eventData.publishAsEvent || false,
        eventEmployees: Array.isArray(eventData.eventEmployees)
          ? eventData.eventEmployees
          : [],
        EventRoles: Array.isArray(eventData.eventRoles)
          ? eventData.eventRoles
          : [],
        EventTags: Array.isArray(eventData.eventTags)
          ? eventData.eventTags
          : [],
        CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
        ReminderBeforeInMins: eventData.reminderBeforeInMins || 0,
        isDraft: eventData.isDraft || false,
        requestedDate: originalRequestedDateForParams,
        timezoneCode: eventData.timezoneCode || moment.tz.guess(),
        EditType: editType || editTypesVariables.allEvent,
        UpdateFlags: {
          isTitleEdit: false,
          isStartTimeEdit: startTimeChanged,
          isEndTimeEdit: endTimeChanged,
          isAllDayEventEdit: false,
          isNoteEdit: false,
          isLocationEdit: false,
          isReminderBeforeInMinsEdit: false,
          isEnterpriseEdit: false,
          isOfficeIdEdit: false,
          EmployeeIds: [],
          RoleIds: [],
          IsRepeatTypeChange: false,
          IsEventDateChange: finalIsEventDateChange,
          IsRepeatedEndDateChange: dateChangedResult.endDateChanged,
          IsBlockForPatientChange: false,
          tagsIds: false,
        },
      };

      if (eventData.isShift) {
        params.Title = eventData.title || "";
        delete params.Location;
        delete params.Note;
        if (
          Array.isArray(eventData.eventEmployees) &&
          eventData.eventEmployees.length > 0 &&
          eventData.eventEmployees[0]
        ) {
          params.officeId = eventData.eventEmployees[0].officeId || null;
        }
      } else {
        params.IsAutoApprove = eventData.isAutoApprove || false;
      }

      try {
        setterCustomSchduler({
          params,
          repeatedType: eventData.repeatedType || 1,
          repeatedTypeFrequency: eventData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: eventData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            eventData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: eventData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            eventData.customRepeatPattern?.monthsOfYear?.map((m) => m?.month) ||
            [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: eventData,
        });
      } catch (error) {
        console.warn("Error in setterCustomSchduler:", error);
      }

      try {
        const preferredOption = {
          dropdown: eventData.publishAsEvent
            ? [{ id: "allowInvitees" }]
            : eventData.isDraft
              ? [{ id: "saveDraft" }]
              : [{ id: "inviteesOnly" }],
          otherOptions: [],
        };
        setterPublishOffices({
          preferredOption,
          userIds: Array.isArray(eventData.eventEmployees)
            ? eventData.eventEmployees
            : [],
          publishAsEvent: eventData.publishAsEvent
            ? "allowInvitees"
            : eventData.isDraft
              ? "saveDraft"
              : "inviteesOnly",
          nonInviteesPublish: eventData.publishedType || "allOffices",
          params,
          officeIdForNonInvitee: Array.isArray(
            eventData.schedulerEventPublishedOffices,
          )
            ? eventData.schedulerEventPublishedOffices.filter(
                (office) => office && office.id,
              )
            : [],
          edit: true,
          isDraft: eventData.isDraft || false,
        });
      } catch (error) {
        console.warn("Error in setterPublishOffices:", error);
      }

      await createSchedulerEventMutation.mutateAsync(params);

      // Refresh the event list
      if (dateRange?.startDate && selectedOwnerId) {
        refetch();
        fetchNotes();
      }

      toast.success(t("staff.eventUpdated"));
    },
    [
      selectedOwnerId,
      createSchedulerEventMutation,
      t,
      getTimeCheck,
      dateRange,
      refetch,
      fetchNotes,
    ],
  );

  // Update event/shift via API (with EditEvent-style checks and modal)
  const updateEventAfterDrag = useCallback(
    async (eventItem, newDateTime, editType = null) => {
      if (!newDateTime || !eventItem || !eventItem.id) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      try {
        const originalRequestedDate = moment(
          draggedEventDateRef.current ||
            draggedEvent?.date ||
            eventItem.startTime ||
            eventItem.date ||
            newDateTime.newDate,
        ).format("YYYY-MM-DD");
        const eventDetails = await getEventListDetails(
          eventItem.id,
          originalRequestedDate,
        );
        const eventData = eventDetails?.data;

        if (!eventData) {
          toast.error(t("scheduler.eventNotFound"));
          return;
        }

        const dateChangedResult = matchedBothDatesValues({
          eventDetail: eventData,
          repeatedEndDate: eventData.repeatedEndDate
            ? moment(eventData.repeatedEndDate).toDate()
            : moment().add(3, "month").toDate(),
          requestedDate: originalRequestedDate,
          eventDate: newDateTime.newDate,
        });

        const repeatedOptionChanged = matchedBothPatternValues({
          repeatedType: eventData.repeatedType || 1,
          repeatedTypeFrequency: eventData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: eventData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            eventData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: eventData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            eventData.customRepeatPattern?.monthsOfYear?.map((m) => m?.month) ||
            [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: eventData,
        });

        const anyChangedForDate = repeatedOptionChanged;

        const startTimeChanged = getTimeCheck(
          eventData.startTime,
          newDateTime.newStartTimeString,
        );
        const endTimeChanged = getTimeCheck(
          eventData.endTime,
          newDateTime.newEndTimeString,
        );

        const hasChanges =
          dateChangedResult.startDateChanged ||
          dateChangedResult.endDateChanged ||
          startTimeChanged ||
          endTimeChanged;

        const neverCheck =
          eventData.repeatedType ===
          addEditEventsConst.repeatedOptionsValues.never;
        if (!neverCheck && eventData.repeatedEndDate) {
          const newDateStr = moment(newDateTime.newDate).format("YYYY-MM-DD");
          const repeatedEndDateStr = moment(eventData.repeatedEndDate).format(
            "YYYY-MM-DD",
          );
          if (
            !moment(newDateStr).isBefore(repeatedEndDateStr) &&
            newDateStr !== repeatedEndDateStr
          ) {
            toast.error(t("form.errors.endGreater"));
            return;
          }
        }

        const isNotDrafted = !eventData.isDraft;

        if (!neverCheck && !editType && hasChanges && isNotDrafted) {
          setPendingDragUpdate({
            eventData,
            newDateTime,
            anyChangedForDate,
            originalRequestedDate,
          });
          setEditModalConfirmation(true);
          return;
        }

        let finalEditType = editType;
        if (!finalEditType) {
          if (neverCheck) {
            finalEditType = editTypesVariables.allEvent;
          } else {
            finalEditType = editTypesVariables.allEvent;
          }
        }

        await performEventUpdate(
          eventData,
          newDateTime,
          finalEditType,
          originalRequestedDate,
        );
      } catch (error) {
        toast.error(error.message || t("scheduler.updateFailed"));
        throw error;
      }
    },
    [t, getTimeCheck, performEventUpdate, draggedEvent],
  );

  // Handle drag start
  const handleDragStart = useCallback(
    (e, eventItem, date) => {
      if (!canDragEvent(eventItem)) {
        e.preventDefault();
        return;
      }

      let originalDate = null;
      if (eventItem.date) {
        originalDate = moment(eventItem.date).startOf("day");
      } else if (eventItem.startTime) {
        originalDate = moment(eventItem.startTime).startOf("day");
      }

      const dragData = {
        eventItem,
        originalDate: originalDate ? originalDate.format("YYYY-MM-DD") : null,
        date: date ? moment(date).format("YYYY-MM-DD") : null,
      };
      draggedEventDateRef.current = date
        ? moment(date).format("YYYY-MM-DD")
        : null;
      // Store in both state (for UI) and ref (for immediate access)
      setDraggedEvent(dragData);
      draggedEventRef.current = dragData;
      setIsDragging(true);

      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      const dragImage = e.currentTarget.cloneNode(true);
      dragImage.style.opacity = "0";
      dragImage.style.position = "absolute";
      dragImage.style.top = "-1000px";
      document.body.appendChild(dragImage);
      e.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
      setTimeout(() => document.body.removeChild(dragImage), 0);
    },
    [canDragEvent],
  );

  // Handle drag over
  const handleDragOver = useCallback(
    (e, date) => {
      if (!draggedEvent) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      // Set preview date when dragging over a date cell
      if (date) {
        let formattedDate = date;
        if (date instanceof Date) {
          formattedDate = moment(date).format("YYYY-MM-DD");
        } else if (date && typeof date.format === "function") {
          formattedDate = date.format("YYYY-MM-DD");
        } else if (typeof date === "string") {
          const parsed = moment(date);
          formattedDate = parsed.isValid() ? parsed.format("YYYY-MM-DD") : date;
        }
        setDragPreviewDate(formattedDate);
      }
    },
    [draggedEvent],
  );

  // Handle drop
  const handleDrop = useCallback(
    async (e, targetDateString) => {
      e.preventDefault();
      e.stopPropagation();

      // Use ref for immediate access (state might be stale)
      const currentDraggedEvent = draggedEventRef.current || draggedEvent;

      if (!currentDraggedEvent || !currentDraggedEvent.eventItem) {
        setIsDragging(false);
        setDraggedEvent(null);
        draggedEventRef.current = null;
        return;
      }

      const { eventItem } = currentDraggedEvent;
      if (!eventItem || !eventItem.id) {
        toast.error(t("scheduler.invalidEventData"));
        setIsDragging(false);
        setDraggedEvent(null);
        return;
      }

      // Ensure targetDateString is in the correct format
      let formattedDate = targetDateString;
      if (targetDateString instanceof Date) {
        formattedDate = moment(targetDateString).format("YYYY-MM-DD");
      } else if (
        targetDateString &&
        typeof targetDateString.format === "function"
      ) {
        // It's a moment object
        formattedDate = targetDateString.format("YYYY-MM-DD");
      } else if (typeof targetDateString === "string") {
        // Try to parse and format it
        const parsed = moment(targetDateString);
        if (parsed.isValid()) {
          formattedDate = parsed.format("YYYY-MM-DD");
        } else {
          console.error("Invalid date string:", targetDateString);
        }
      } else {
        // Try to parse as moment
        const parsed = moment(targetDateString);
        if (parsed.isValid()) {
          formattedDate = parsed.format("YYYY-MM-DD");
        } else {
          console.error("Invalid date format:", targetDateString);
        }
      }

      // Check if dropped on same date - if so, don't call API
      const originalDateForCheck = currentDraggedEvent?.originalDate
        ? moment(currentDraggedEvent.originalDate).startOf("day")
        : eventItem.date
          ? moment(eventItem.date).startOf("day")
          : eventItem.startTime
            ? moment(eventItem.startTime).startOf("day")
            : null;

      if (
        originalDateForCheck &&
        moment(formattedDate).isSame(originalDateForCheck, "day")
      ) {
        setIsDragging(false);
        setDraggedEvent(null);
        draggedEventRef.current = null;
        return;
      }

      const newDateTime = calculateNewDateForMonthly(eventItem, formattedDate);

      if (!newDateTime) {
        const today = moment().startOf("day");
        const originalDate = currentDraggedEvent.originalDate
          ? moment(currentDraggedEvent.originalDate)
          : eventItem.date
            ? moment(eventItem.date)
            : eventItem.startTime
              ? moment(eventItem.startTime)
              : null;
        const isOriginalDatePast =
          originalDate && originalDate.isBefore(today, "day");

        const newDateMoment = moment(targetDateString);
        const isNewDatePast = newDateMoment.isBefore(today, "day");

        if (
          (!isOriginalDatePast && isNewDatePast) ||
          (isOriginalDatePast && isNewDatePast)
        ) {
          toast.error("Cannot drag to past date");
        } else if (originalDate && newDateMoment.isSame(originalDate, "day")) {
          // Same date - no error needed
        }
        setIsDragging(false);
        setDraggedEvent(null);
        return;
      }

      setIsDragDropLoading(true);
      try {
        await updateEventAfterDrag(eventItem, newDateTime);
      } catch (error) {
        console.error("Error in updateEventAfterDrag", error);
        // Error already shown in update functions
      } finally {
        setIsDragDropLoading(false);
        setIsDragging(false);
        setDraggedEvent(null);
        draggedEventRef.current = null;
        setDragPreviewDate(null);
      }
    },
    [draggedEvent, calculateNewDateForMonthly, updateEventAfterDrag, t],
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (!isDragDropLoading) {
      setIsDragging(false);
      setDraggedEvent(null);
      draggedEventRef.current = null;
      setDragPreviewDate(null);
    }
  }, [isDragDropLoading]);

  const deleteNotesConfirm = async () => {
    setdeleteConfirmation({
      visible: false,
      id: "",
    });
    setIsLoading(true);

    try {
      let res = await deleteSchedularNote(deleteConfirmation.id);
      if (res.statusCode === 200) {
        updateList("", "DELETE");
        toast.success(res?.message);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      toast.error(e.message);
    }
  };

  const deleteModal = useMemo(() => {
    return (
      <DeleteNotes
        isDeleteEventModalOpen={deleteConfirmation.visible}
        setIsDeleteEventModalOpen={setdeleteConfirmation}
        deleteNotesConfirm={deleteNotesConfirm}
        updateList={updateList}
      />
    );
    // eslint-disable-next-line
  }, [deleteConfirmation]);
  const simpleDeleteConfirmModal = renderSimpleDeleteConfirmModal();
  const { search } = useLocation();
  const dateMatchForHighlight = useMemo(() => {
    const searchValue = new URLSearchParams(search).get(showTime);
    if (searchValue?.includes("NaN") || !search) {
      return null;
    }
    return new Date(+searchValue);
  }, [search]);

  // Filter noteEventListing based on eventShiftFilter or appliedPublishedDraftFilter before rendering
  const getFilteredListing = (listing) => {
    // Show all if no filter, or both 'event' and 'shift' are selected
    // Only one filter is selected
    return listing.map((day) => {
      if (!day.schedulerEvents) return day;
      const eventsFilter = eventShiftFilter.includes("event");
      const shiftFilter = eventShiftFilter.includes("shift");
      const shiftByOtherFilter = eventShiftFilter.includes("shift-others");
      return {
        ...day,
        schedulerEvents: day.schedulerEvents
          .filter((ev) => {
            if (!appliedPublishedDraftFilter.length) return true;
            if (appliedPublishedDraftFilter.includes("draft") && ev.isDraft)
              return true;
            if (
              appliedPublishedDraftFilter.includes("published") &&
              !ev.isDraft
            )
              return true;
            return false;
          })
          .filter((ev) => {
            if (!eventShiftFilter.length) return true;
            if (eventsFilter && !ev.isShift) return true;
            if (
              shiftFilter &&
              ev.isShift &&
              (shiftByOtherFilter || ev.createdById === profile.id)
            )
              return true;
            if (
              shiftByOtherFilter &&
              ev.isShift &&
              ev.createdById !== profile.id
            )
              return true;
            return false;
          }),
      };
    });
  };

  const divRef = useRef(null);

  const listingForPublish = useMemo(() => {
    let listing = getFilteredListing(noteEventListing);

    if (isEnterpriseFilterActive) {
      listing = (listing || []).map((item) => {
        if (!item.schedulerEvents) return item;
        return {
          ...item,
          schedulerEvents: item.schedulerEvents.filter(
            (e) => e && e.isEnterprise === true,
          ),
        };
      });
    }

    if (searchValue && listing?.length) {
      listing = listing.map((item) => {
        const titleList = searchValue?.trim()
          ? item?.schedulerEvents?.filter((data) => {
              if (
                data.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
                data?.eventTags
                  ?.toLowerCase()
                  .includes(searchValue.toLowerCase())
              ) {
                return data;
              }
              return false;
            })
          : item?.schedulerEvents;
        return {
          ...item,
          schedulerEvents: titleList || [],
        };
      });
    }

    return listing;
  }, [
    noteEventListing,
    eventShiftFilter,
    appliedPublishedDraftFilter,
    isEnterpriseFilterActive,
    searchValue,
    profile?.id,
  ]);

  useEffect(() => {
    if (!onVisibleDraftEventsUpdate) return;
    onVisibleDraftEventsUpdate(
      collectDraftPublishItemsFromWeekData(listingForPublish),
    );
  }, [listingForPublish, onVisibleDraftEventsUpdate]);

  useEffect(() => {
    if (!onVisibleClearableItemsUpdate) return;
    const clearableItems = [];
    (listingForPublish || []).forEach((day) => {
      (day.schedulerEvents || []).forEach((ev) => {
        if (ev?.id != null) {
          clearableItems.push({ id: ev.id, isShift: Boolean(ev.isShift) });
        }
      });
    });
    onVisibleClearableItemsUpdate(clearableItems);
  }, [listingForPublish, onVisibleClearableItemsUpdate]);

  useEffect(() => {
    if (!handleSelectableEventsUpdate) return;
    const eventsMap = {};
    (listingForPublish || []).forEach((day) => {
      const requestedDate = moment(day.date).format("YYYY-MM-DD");
      (day.schedulerEvents || []).forEach((ev) => {
        const key = getEventShiftSelectionKey(ev.id, requestedDate);
        eventsMap[key] = {
          id: ev.id,
          date: requestedDate,
          isDraft: ev.isDraft,
          isShift: Boolean(ev.isShift),
          publishAsEvent: ev.publishAsEvent,
          userId: null,
          isUnassigned: isUnassignedSchedulerEvent(ev),
        };
      });
    });
    handleSelectableEventsUpdate(eventsMap);
  }, [listingForPublish, handleSelectableEventsUpdate]);

  /**
   * Height given to every date cell so the weeks of the month share the
   * available viewport evenly. It is deliberately independent of the events a
   * cell holds: each cell then has a fixed amount of room that the fit
   * calculation can fill with as many matching cards as the active display
   * filters allow.
   */
  const monthlyCellHeight = useMemo(() => {
    const rowCount = Math.ceil((noteEventListing?.length ?? 0) / columnsPerRow);
    if (!gridHeight || !rowCount) return MONTHLY_MIN_CELL_HEIGHT;
    return Math.max(MONTHLY_MIN_CELL_HEIGHT, Math.floor(gridHeight / rowCount));
  }, [gridHeight, noteEventListing?.length, columnsPerRow]);

  const MonthlyViewTable = useMemo(() => {
    // Start with the base filtered listing
    let filteredListing = getFilteredListing(noteEventListing); // Use noteEventListing instead of noteEventListingFilter

    // Apply enterprise filter if active
    if (isEnterpriseFilterActive) {
      filteredListing = (filteredListing || []).map((item) => {
        if (!item.schedulerEvents) return item;
        return {
          ...item,
          schedulerEvents: item.schedulerEvents.filter(
            (e) => e && e.isEnterprise === true,
          ),
        };
      });
    }

    // Apply search filter if searchValue exists
    if (searchValue && filteredListing?.length) {
      filteredListing = filteredListing.map((item) => {
        let titleList = searchValue?.trim()
          ? item?.schedulerEvents &&
            item?.schedulerEvents?.length &&
            item.schedulerEvents?.filter((data) => {
              if (
                data.title.toLowerCase().includes(searchValue.toLowerCase()) ||
                (data?.eventTags &&
                  data.eventTags
                    .toLowerCase()
                    .includes(searchValue.toLowerCase()))
              ) {
                return data;
              }
            })
          : item?.schedulerEvents;
        return {
          ...item,
          schedulerEvents: titleList || [],
        };
      });
    }

    if (viewSettingFilter?.length > 0 && filteredListing?.length) {
      const filterByRequestToJoin = viewSettingFilter.includes("requestTojoin");
      const filterByAssignedEmployees =
        viewSettingFilter.includes("assignedEmployees");
      if (filterByRequestToJoin || filterByAssignedEmployees) {
        filteredListing = filteredListing.map((item) => {
          if (!item.schedulerEvents) return item;
          const events = item.schedulerEvents.filter((ev) => {
            const hasRequestToJoin =
              ev?.publishAsEvent && (ev?.eventRequestToJoinCount ?? 0) > 0;
            const hasAssignedEmployees = (ev?.eventEmployeeCount ?? 0) > 0;
            if (filterByRequestToJoin && filterByAssignedEmployees) {
              return hasRequestToJoin || hasAssignedEmployees;
            }
            if (filterByRequestToJoin) return hasRequestToJoin;
            if (filterByAssignedEmployees) return hasAssignedEmployees;
            return true;
          });
          return { ...item, schedulerEvents: events };
        });
      }
    }

    return (
      <div
        ref={bodyRowRef}
        className={styles["monthly-tr"] + " " + styles["monthly-body-row"]}
      >
        {filteredListing?.map((list, index) => {
          const displayDate = list.date ?? list.requestedDate;
          const highlightCheck =
            dateMatchForHighlight &&
            moment(dateMatchForHighlight)
              .startOf("day")
              .isSame(moment(displayDate).startOf("day"));
          const existingHoliday =
            holidaysObject[moment(displayDate).format("DD-MM-YYYY")];
          const classFOrHoliday = existingHoliday
            ? styles["light-red-bg"]
            : styles["no-theme-bg"];
          const showHolidayWhenBlinkingStopped = highlightCheck
            ? "blinking-background"
            : classFOrHoliday;
          const holidayElements = existingHoliday && (
            <div className={styles.holiday_title}>
              <img
                src={HolidayImage}
                alt="holiday"
                className="account_owner_image"
              />
              <div
                className={styles.holiday_title_text + " text-break text-wrap"}
              >
                {existingHoliday}
              </div>
            </div>
          );
          const dateShow = moment(displayDate).format("DD");
          if (list?.schedulerEvents?.length) {
            return (
              <DataMappingWithListing
                highlightCheck={highlightCheck}
                divRef={divRef}
                eventCardListing={eventCardListing}
                list={list}
                assignEmpModel={assignEmpModel}
                showHolidayWhenBlinkingStopped={showHolidayWhenBlinkingStopped}
                displayDate={displayDate}
                dateShow={dateShow}
                cellHeight={monthlyCellHeight}
                canAddShift={canAddShift}
                t={t}
                noteImage={noteImage}
                index={index}
                holidayElements={holidayElements}
                handleEventList={handleEventList}
                moveToDetailPage={moveToDetailPage}
                viewSettingFilter={viewSettingFilter}
                assignedEmployeesToggle={assignedEmployeesToggle}
                setAssignEmpModel={setAssignEmpModel}
                setEventCardListing={setEventCardListing}
                classFOrHoliday={classFOrHoliday}
                profile={profile}
                history={history}
                canDragEvent={canDragEvent}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                handleDragOver={handleDragOver}
                handleDrop={handleDrop}
                isDragging={isDragging}
                draggedEvent={draggedEvent}
                dragPreviewDate={dragPreviewDate}
                setDragPreviewDate={setDragPreviewDate}
                onDuplicateEvent={onDuplicateEvent}
                onAssignUsers={openAssignUsersModal}
                onDeleteEvent={handleCardDeleteClick}
                bulkSelectMode={bulkSelectMode}
                selectedEvents={selectedEvents}
                handleEventSelectionToggle={handleEventSelectionToggle}
              />
            );
          }
          return (
            <div
              ref={highlightCheck ? divRef : undefined}
              className={
                styles["monthly-col"] +
                " " +
                styles["monthly-td"] +
                " " +
                showHolidayWhenBlinkingStopped
              }
              style={{ minHeight: monthlyCellHeight }}
              key={list.date}
              onDragOver={(e) => {
                if (handleDragOver) {
                  handleDragOver(e, displayDate);
                }
              }}
              onDragLeave={(e) => {
                // Only clear preview if leaving the cell entirely (not just moving to a child)
                const relatedTarget = e.relatedTarget;
                if (
                  !relatedTarget ||
                  !e.currentTarget.contains(relatedTarget)
                ) {
                  setDragPreviewDate(null);
                }
              }}
              onDrop={(e) => {
                if (handleDrop) {
                  // Ensure displayDate is formatted correctly
                  let dateToPass = displayDate;
                  if (displayDate instanceof Date) {
                    dateToPass = moment(displayDate).format("YYYY-MM-DD");
                  } else if (
                    displayDate &&
                    typeof displayDate.format === "function"
                  ) {
                    // It's a moment object
                    dateToPass = displayDate.format("YYYY-MM-DD");
                  } else if (typeof displayDate === "string") {
                    // Already a string, validate it
                    const parsed = moment(displayDate);
                    dateToPass = parsed.isValid()
                      ? parsed.format("YYYY-MM-DD")
                      : displayDate;
                  } else {
                    // Try to parse
                    const parsed = moment(displayDate);
                    dateToPass = parsed.isValid()
                      ? parsed.format("YYYY-MM-DD")
                      : moment(displayDate).format("YYYY-MM-DD");
                  }
                  handleDrop(e, dateToPass);
                }
              }}
            >
              <div className={styles["date-box"]}>
                {list?.note && <NoteView list={list} dateShow={dateShow} />}
                {dateShow}
                <span className={styles["mobile-week-days"]}>
                  ({getWeekDay(list?.date)})
                </span>
              </div>
              {/* Drag Preview Card for empty cells */}
              {isDragging &&
              draggedEvent?.eventItem &&
              dragPreviewDate &&
              moment(displayDate).format("YYYY-MM-DD") === dragPreviewDate ? (
                <div
                  className={styles["scheduler-event-box"]}
                  style={{
                    backgroundColor: "#C8E36433",
                    border: "2px dashed #C8E364",
                    borderRadius: "6px",
                    opacity: 0.6,
                    pointerEvents: "none",
                    marginBottom: "4px",
                  }}
                >
                  <div className="d-flex justify-content-between">
                    <div className="d-flex gap-1">
                      {draggedEvent.eventItem?.isDraft && (
                        <div
                          className={
                            styles["event-tag"] +
                            " " +
                            styles["draft-warning-bg"]
                          }
                          style={{ opacity: 0.6 }}
                        >
                          {t("scheduler.isDraft")}
                        </div>
                      )}
                      {!(
                        draggedEvent.eventItem?.isShift &&
                        profile?.id !== draggedEvent.eventItem?.createdById
                      ) && (
                        <div
                          className={
                            styles["event-tag"] + " " + styles["theme-green-bg"]
                          }
                          style={{ opacity: 0.6 }}
                        >
                          {draggedEvent.eventItem?.isShift
                            ? t("scheduler.agenda.shiftTag")
                            : t("scheduler.agenda.eventTag")}
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      <div
                        className={styles.ellipsis_icon}
                        style={{ opacity: 0.6 }}
                      >
                        <ThreeDots />
                      </div>
                    </div>
                  </div>
                  {draggedEvent.eventItem?.isShift &&
                    profile?.id !== draggedEvent.eventItem?.createdById && (
                      <div
                        className={
                          styles["event-tag"] +
                          " " +
                          styles["theme-green-bg"] +
                          " my-1"
                        }
                        style={{ opacity: 0.6 }}
                      >
                        {t("scheduler.agenda.shiftByOthers")}
                      </div>
                    )}
                  {(!viewSettingFilter.length ||
                    viewSettingFilter.includes("timeString")) && (
                    <div
                      className={styles["event-date"]}
                      style={{ opacity: 0.6 }}
                    >
                      {draggedEvent.eventItem?.isAllDayEvent
                        ? t("scheduler.agenda.allDayEvent")
                        : draggedEvent.eventItem?.startTime &&
                            draggedEvent.eventItem?.endTime
                          ? moment(draggedEvent.eventItem?.startTime).format(
                              "h:mm A",
                            ) +
                            " - " +
                            moment(draggedEvent.eventItem?.endTime).format(
                              "h:mm A",
                            )
                          : ""}
                      {draggedEvent.eventItem?.timezoneCode &&
                        ` (${draggedEvent.eventItem?.timezoneCode})`}
                    </div>
                  )}
                  {(!viewSettingFilter.length ||
                    viewSettingFilter.includes("officeName")) && (
                    <div
                      className={styles["event-office"]}
                      style={{ opacity: 0.6 }}
                    >
                      {renderOfficeNameOrEnterprise(draggedEvent.eventItem, t)}
                    </div>
                  )}
                  <div
                    className={styles["event-title"]}
                    style={{ opacity: 0.6 }}
                    title={draggedEvent.eventItem?.title}
                  >
                    {titleWordLimit(draggedEvent.eventItem?.title, 20)}
                  </div>
                </div>
              ) : (
                <div
                  className={
                    styles["empty-event"] +
                    (holidayElements ? " flex-column" : "")
                  }
                  style={
                    !bulkSelectMode && !isDragging
                      ? { cursor: "pointer" }
                      : undefined
                  }
                  onClick={() => {
                    if (bulkSelectMode || isDragging) return;
                    history.push(constants.routes.scheduler.addEvent, {
                      date: moment(displayDate).format("YYYY-MM-DD"),
                    });
                  }}
                >
                  {holidayElements}
                  {!bulkSelectMode && !isDragging && (
                    <EmptySchedulerCellAdd
                      ariaLabel={t(
                        canAddShift
                          ? "accountOwner.newEventShift"
                          : "accountOwner.newEvent",
                      )}
                      onClick={() =>
                        history.push(constants.routes.scheduler.addEvent, {
                          date: moment(displayDate).format("YYYY-MM-DD"),
                        })
                      }
                    />
                  )}
                </div>
              )}
              <UncontrolledDropdown className={styles.ellipsis_position}>
                <DropdownToggle
                  caret={false}
                  tag="div"
                  className="cursor-pointer"
                >
                  <div className={styles.ellipsis_icon + " " + `ellipsis-icon`}>
                    <ThreeDots />
                  </div>
                </DropdownToggle>

                <DropdownMenu end className={styles["office-dropdown-menu"]}>
                  <DropdownItem className={styles["office-dropdown-item"]}>
                    <Link
                      to={{
                        pathname: constants.routes.scheduler.addEvent,
                        state: {
                          date: moment(displayDate).format("YYYY-MM-DD"),
                        },
                      }}
                      className={styles["add-event-btn"]}
                    >
                      {t(
                        canAddShift
                          ? "accountOwner.newEventShift"
                          : "accountOwner.newEvent",
                      )}
                    </Link>
                  </DropdownItem>
                  <DropdownItem className={styles["office-dropdown-item"]}>
                    {noteImage(list, index)}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        })}
      </div>
    );
  }, [
    monthDates,
    noteEventListing,
    monthlyCellHeight,
    eventCardListing,
    assignEmpModel,
    searchValue,
    viewSettingFilter,
    eventShiftFilter,
    appliedPublishedDraftFilter,
    isEnterpriseFilterActive,
    appliedPublishedDraftFilter,
    search,
    canDragEvent,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    isDragging,
    draggedEvent,
    dragPreviewDate,
    setDragPreviewDate,
    holidaysObject,
    dateMatchForHighlight,
    profile,
    history,
    t,
    canAddShift,
    onDuplicateEvent,
    openAssignUsersModal,
    handleCardDeleteClick,
    bulkSelectMode,
    selectedEvents,
    handleEventSelectionToggle,
  ]);

  useEffect(() => {
    // Get the filtered listing after all filters
    let filteredListing = getFilteredListing(noteEventListingFilter);

    // If enterprise filter is active, apply it
    if (isEnterpriseFilterActive) {
      filteredListing = (filteredListing || []).map((item) => {
        if (!item.schedulerEvents) return item;
        return {
          ...item,
          schedulerEvents: item.schedulerEvents.filter(
            (e) => e && e.isEnterprise === true,
          ),
        };
      });
    }

    // If searchValue is present, apply search filter
    let finalFilteredListing = filteredListing;
    if (searchValue && filteredListing?.length) {
      finalFilteredListing = filteredListing.map((item) => {
        let titleList =
          item?.schedulerEvents &&
          item?.schedulerEvents?.length &&
          item.schedulerEvents?.filter((data) => {
            if (
              data.title.toLowerCase().includes(searchValue.toLowerCase()) ||
              (data?.eventTags &&
                data.eventTags
                  .toLowerCase()
                  .includes(searchValue.toLowerCase()))
            ) {
              return data;
            }
          });
        return {
          ...item,
          schedulerEvents: titleList || [],
        };
      });
    }

    // Count total events in the filtered listing
    let filteredCount = 0;
    finalFilteredListing.forEach((item) => {
      filteredCount += item?.schedulerEvents?.length || 0;
    });

    settotalEvents(filteredCount);
  }, [
    noteEventListingFilter,
    eventShiftFilter,
    appliedPublishedDraftFilter,
    isEnterpriseFilterActive,
    searchValue,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const targetTime = queryParams.get(showTime);

    if (!targetTime || !noteEventListing?.length) {
      isHighlightingRef.current = false;
      lastHighlightedRef.current = null;
      return;
    }

    // Only fire this entire sequence once per target Time
    if (lastHighlightedRef.current === targetTime) {
      return;
    }

    // Lock dynamic table height / viewport-based maxHeight until the user scrolls manually.
    lastHighlightedRef.current = targetTime;
    isHighlightingRef.current = true;

    let scrollTimeout;
    let attachListenersTimeout;

    // Removes URL highlight, re-enables dynamic height, and detaches listeners.
    const stopHighlight = () => {
      const currentParams = new URLSearchParams(history.location.search);
      if (currentParams.has(showTime)) {
        currentParams.delete(showTime);
        history.replace({ search: currentParams.toString() });
      }

      isHighlightingRef.current = false;
      window.dispatchEvent(new Event("resize"));

      window.removeEventListener("wheel", stopHighlight, true);
      window.removeEventListener("touchmove", stopHighlight, true);
      window.removeEventListener("keydown", stopHighlight, true);
      window.removeEventListener("scroll", stopHighlight, true);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener(
          "scroll",
          stopHighlight,
          true,
        );
      }
    };

    const attachUserScrollListeners = () => {
      window.addEventListener("wheel", stopHighlight, {
        capture: true,
        once: true,
        passive: true,
      });
      window.addEventListener("touchmove", stopHighlight, {
        capture: true,
        once: true,
        passive: true,
      });
      window.addEventListener("keydown", stopHighlight, {
        capture: true,
        once: true,
        passive: true,
      });
      window.addEventListener("scroll", stopHighlight, {
        capture: true,
        once: true,
        passive: true,
      });
      if (scrollContainerRef.current) {
        scrollContainerRef.current.addEventListener("scroll", stopHighlight, {
          capture: true,
          once: true,
          passive: true,
        });
      }
    };

    scrollTimeout = setTimeout(() => {
      if (!divRef.current) {
        isHighlightingRef.current = false;
        lastHighlightedRef.current = null;
        window.dispatchEvent(new Event("resize"));
        return;
      }

      isHighlightingRef.current = true;

      divRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });

      // After smooth scrollIntoView (and any layout from the locked height), attach
      // listeners so programmatic scroll does not dismiss the highlight. Dynamic
      // maxHeight stays off until the first manual scroll (window or grid container).
      attachListenersTimeout = setTimeout(attachUserScrollListeners, 1300);
    }, 500);

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (attachListenersTimeout) clearTimeout(attachListenersTimeout);
      window.removeEventListener("wheel", stopHighlight, true);
      window.removeEventListener("touchmove", stopHighlight, true);
      window.removeEventListener("keydown", stopHighlight, true);
      window.removeEventListener("scroll", stopHighlight, true);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener(
          "scroll",
          stopHighlight,
          true,
        );
      }
    };
  }, [search, noteEventListing?.length, history]);

  return (
    <div
      className={styles["montly-calendar-wrapper"]}
      ref={scrollContainerRef}
      style={{
        maxHeight: tableHeight,
        overflowY: "auto",
        position: "relative",
      }}
    >
      {(isLoading ||
        notesLoad ||
        isLoader ||
        isFetching ||
        isDragDropLoading ||
        isAssignLoading) && <Loader />}

      <div
        className={styles["monthly-tr"]}
        ref={headerRef}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 105,
          backgroundColor: "#ffffff",
        }}
      >
        {constants.weekday?.map((item) => (
          <div
            className={styles["monthly-th"] + " " + styles["monthly-col"]}
            key={item}
          >
            {item?.slice(0, 3)}
          </div>
        ))}
      </div>
      {MonthlyViewTable}
      {addModal}
      {editModal}
      {deleteModal}
      {simpleDeleteConfirmModal}
      {renderAssignUsersModals()}
      {editModalConfirmation && pendingDragUpdate && (
        <EditOptionsModal
          isOpen={editModalConfirmation}
          closeStepOneModal={setEditModalConfirmation}
          loader={isDragDropLoading}
          eventKey={{
            eventOrShift: t(
              `accountOwner.${
                pendingDragUpdate.eventData?.isShift ? "shift" : "event"
              }Tab`,
            ).toLowerCase(),
          }}
          eventOrShiftTab={pendingDragUpdate.eventData?.isShift || false}
          busySlots={false}
          repeatedOptionChanged={pendingDragUpdate.anyChangedForDate || false}
          handleConfirm={async (value) => {
            setIsDragDropLoading(true);
            try {
              await performEventUpdate(
                pendingDragUpdate.eventData,
                pendingDragUpdate.newDateTime,
                value,
                pendingDragUpdate.originalRequestedDate,
              );
            } catch {
            } finally {
              setEditModalConfirmation(false);
              setPendingDragUpdate(null);
              setIsDragDropLoading(false);
            }
          }}
        />
      )}
    </div>
  );
}

export default withTranslation()(EventsShiftsMonthly);
